const ISSUE_TYPE_LABELS = {
  storm_damage: 'Storm Damage',
  water_intrusion: 'Water Intrusion',
  active_leak: 'Active Leak',
  roof_leak: 'Roof Leak',
  flood: 'Flood',
  fire: 'Fire / Smoke Damage',
  mold: 'Mold',
  water: 'Water Damage',
  no_heat: 'No Heat',
  no_ac: 'No AC',
  hvac_down: 'HVAC Outage',
  elevator_down: 'Elevator Out of Service',
  elevator_entrapment: 'Elevator Entrapment',
  electrical_outage: 'Electrical Outage',
};

const URGENCY_LABELS = {
  urgent: 'Critical — Immediate Response',
  high: 'High Priority',
  medium: 'Standard',
  normal: 'Standard',
  low: 'Low Priority',
};

export const formatIssueTypeLabel = (issueType) => {
  if (!issueType) return 'Property Issue';
  const key = String(issueType).trim().toLowerCase();
  if (ISSUE_TYPE_LABELS[key]) return ISSUE_TYPE_LABELS[key];
  return key
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

export const formatUrgencyLabel = (priority) => {
  if (!priority) return 'Standard';
  const key = String(priority).trim().toLowerCase();
  return URGENCY_LABELS[key] || URGENCY_LABELS.medium;
};

export const formatIssueTypeDescription = (issueType, category) => {
  const key = String(issueType || '').trim().toLowerCase();
  const descriptions = {
    storm_damage:
      'Storm-related exterior or structural damage requiring inspection and mitigation',
    water_intrusion: 'Water entering the property requiring immediate mitigation',
    active_leak: 'Active water leak requiring immediate professional intervention',
    roof_leak: 'Roof leak requiring inspection and repair',
    flood: 'Flooding or standing water requiring emergency response',
    fire: 'Fire or smoke damage requiring assessment and restoration',
    mold: 'Mold conditions requiring professional remediation',
  };
  if (descriptions[key]) return descriptions[key];
  if (category) {
    return `${formatIssueTypeLabel(category)} issue requiring professional assessment`;
  }
  return 'Property issue requiring professional assessment';
};

const coalesceActionsTaken = (content = {}, issueContext = {}) => {
  const fromContent = Array.isArray(content.actionsTaken) ? content.actionsTaken : [];
  const fromContext = Array.isArray(issueContext.actionsTaken) ? issueContext.actionsTaken : [];
  const merged = [...fromContent, ...fromContext].filter(Boolean);
  return [...new Set(merged)];
};

const formatEntryWindow = ({ requestedServiceWindow, startBy, endBy }) => {
  if (requestedServiceWindow) return requestedServiceWindow;
  if (startBy || endBy) {
    const parts = [];
    if (startBy) {
      parts.push(
        new Date(startBy).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })
      );
    }
    if (endBy) {
      parts.push(
        new Date(endBy).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })
      );
    }
    return parts.join(' — ');
  }
  return 'ASAP';
};

/**
 * Returns the matched response partner when issue classification succeeded.
 */
export const getClassifiedResponsePartner = (content = {}) => {
  if (content.issueClassificationStatus !== 'classified') return null;
  return content.issueContext?.responsePartner ?? null;
};

/**
 * Build a job-order review payload from maintenance ticket action metaData.
 */
export const buildJobOrderFromMetaData = (metaData) => {
  if (!metaData) return null;

  const { content = {}, title, startBy, endBy, maintenanceTicketId } = metaData;
  const issueContext = content.issueContext || {};
  const partner = issueContext.responsePartner;
  if (!partner) return null;

  const primaryIssueType = issueContext.primaryIssueType || content.primaryIssueType;
  const priority = content.priority || issueContext.urgencyDescription;
  const propertyName = content.propertyName || issueContext.propertyName;
  const unitLabel = content.unitLabel || issueContext.unitLabel;
  const locationDetails = content.locationDetails || issueContext.locationDetails;
  const requestedServiceWindow =
    content.requestedServiceWindow || issueContext.requestedServiceWindow;

  const contact = issueContext.preferredContact || content.requester || {};
  const actionsTaken = coalesceActionsTaken(content, issueContext);

  return {
    maintenanceTicketId,
    title,
    partnerId: partner.id,
    partnerSlug: partner.slug,
    vendorName: partner.name,
    recipientEmails: partner.defaultDispatchEmails ?? [],
    eventType: formatIssueTypeLabel(primaryIssueType),
    urgencyLevel: formatUrgencyLabel(priority),
    eventDescription: formatIssueTypeDescription(primaryIssueType, content.category),
    address: propertyName,
    building: unitLabel,
    locationDetails,
    entryWindow: formatEntryWindow({ requestedServiceWindow, startBy, endBy }),
    summary: content.description || issueContext.issueSummary || title,
    actionsTaken,
    hasMedia: Boolean(content.hasMedia ?? issueContext.hasMedia),
    mediaNote: content.mediaNote || issueContext.mediaNote || null,
    insuranceClaim: content.insuranceClaim || issueContext.insuranceClaim || null,
    contact: {
      name: contact.name || null,
      phone: contact.phone || null,
      email: contact.email || null,
    },
    permissionToEnter:
      typeof content.permissionToEnter === 'boolean'
        ? content.permissionToEnter
        : issueContext.permissionToEnter,
    accessInstructions: content.accessInstructions || issueContext.accessInstructions || null,
    discoveredAt: content.discoveredAt || issueContext.discoveredAt || null,
    primaryIssueType,
    routingRuleId: issueContext.routingRule?.id ?? null,
  };
};

export const formatPartnerSpecialtyLine = (content = {}, partner = {}) => {
  const primaryIssueType = content.issueContext?.primaryIssueType || content.primaryIssueType;
  const tradeHint = content.tradeHint || content.issueContext?.tradeHint;
  const parts = [];
  if (primaryIssueType) parts.push(formatIssueTypeLabel(primaryIssueType));
  if (tradeHint) {
    const trade =
      tradeHint.charAt(0).toUpperCase() + String(tradeHint).slice(1).replace(/_/g, ' ');
    if (!parts.some((p) => p.toLowerCase() === trade.toLowerCase())) {
      parts.push(trade);
    }
  }
  if (parts.length === 0 && partner.serviceLines?.length) {
    parts.push(formatIssueTypeLabel(partner.serviceLines[0]));
  }
  return parts.join(' • ');
};
